import { useEffect, useState } from 'react'

import { useVoiceCall } from './hooks/useVoiceCall'
import { callApi } from './services/callApi'
import { consumeLaunchTicket } from './services/launchTicket'
import type { LaunchContext } from './types'
import './app.css'

const phaseText = {
  bootstrapping: '正在确认来电身份',
  ready: '准备好后，轻触接通',
  permission: '正在请求麦克风',
  preparing: '正在准备房间',
  'entering-room': '正在接通信号',
  'waiting-bot': '等待对方接听',
  connected: '通话中',
  ending: '正在挂断',
  ended: '通话已结束',
  unsupported: '当前设备暂不支持',
  error: '连接没有成功',
} as const

function formatDuration(seconds: number) {
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function VoiceCall({ launch }: { launch: LaunchContext }) {
  const call = useVoiceCall(launch)
  const canStart = call.phase === 'ready'
  const shouldReturn = ['error', 'unsupported', 'ended'].includes(call.phase)
  const active = ['preparing', 'entering-room', 'waiting-bot', 'connected'].includes(call.phase)
  const stateLabel = call.aiState === 2 ? '正在想你说的话' : call.aiState === 3 ? '正在回应' : ''

  return (
    <main className="call-shell">
      <div className="night-sky" aria-hidden="true"><i /><i /><i /></div>
      <header className="call-header">
        <span className="brand">HUGMI · NIGHT LINE</span>
        <time>{call.phase === 'connected' ? formatDuration(call.elapsedSeconds) : '···'}</time>
      </header>

      <section className="identity" aria-live="polite">
        <div className={`signal-orbit ${call.phase === 'connected' ? 'signal-orbit--live' : ''}`}>
          <div className="avatar-frame">
            <img src={launch.character.avatar} alt={launch.character.name} />
          </div>
        </div>
        <p className="eyebrow">{phaseText[call.phase]}</p>
        <h1>{launch.character.name}</h1>
        <p className="state-copy">{stateLabel || call.error || '这是一条只属于你们的安静线路'}</p>
      </section>

      <section className="subtitles" aria-label="实时字幕">
        {call.subtitles.map(line => (
          <p key={line.key} className={line.speaker === 'user' ? 'subtitle subtitle--me' : 'subtitle'}>
            <span>{line.speaker === 'user' ? '你' : launch.character.name}</span>{line.text}
          </p>
        ))}
      </section>

      {call.needsAudioResume ? (
        <button className="resume-audio" onClick={() => void call.resumeAudio()}>
          恢复声音播放
        </button>
      ) : null}

      <footer className="call-controls">
        {canStart ? (
          <button className="answer-button" onClick={() => void call.start()}>
            <span className="phone-glyph">⌁</span>
            接通
          </button>
        ) : shouldReturn ? (
          <button className="answer-button" onClick={() => void call.end('client_error')}>
            返回 Hugmi
          </button>
        ) : (
          <>
            <button
              className={`control ${call.muted ? 'control--active' : ''}`}
              disabled={!active}
              onClick={() => void call.toggleMute()}
            >
              <span>{call.muted ? '×' : '◉'}</span>{call.muted ? '已静音' : '麦克风'}
            </button>
            <button className="hangup" onClick={() => void call.end()} aria-label="挂断通话">⌁</button>
            <button
              className={`control ${call.soundMuted ? 'control--active' : ''}`}
              disabled={!active}
              onClick={() => void call.toggleSound()}
            >
              <span>{call.soundMuted ? '×' : '◌'}</span>{call.soundMuted ? '无声音' : '声音'}
            </button>
          </>
        )}
      </footer>
    </main>
  )
}

export default function App() {
  const [launch, setLaunch] = useState<LaunchContext>()
  const [error, setError] = useState('')

  useEffect(() => {
    const ticket = consumeLaunchTicket()
    if (!ticket) {
      setError('通话链接无效，请返回 Hugmi 重新发起')
      return
    }
    void callApi.exchange(ticket).then(setLaunch).catch(caught => {
      setError(caught instanceof Error ? caught.message : '通话链接已失效')
    })
  }, [])

  if (!launch) {
    return (
      <main className="call-shell call-shell--loading">
        <div className="loader"><i /><i /><i /></div>
        <p>{error || '正在接入夜间线路…'}</p>
      </main>
    )
  }
  return <VoiceCall launch={launch} />
}
